import React, { useMemo, useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import MultiTrendChart from "../../Components/Charts/MultiTrendChart";
import { Pager, SOURCE_ICONS, StatCards, chipClass } from "./shared";
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ClockIcon,
  EyeIcon,
  FilterIcon,
  HistoryIcon,
  MoreIcon,
  PlayIcon,
  PlusIcon,
  SearchIcon,
} from "../../Components/Icons";
import {
  NEXT_SYNCS,
  PERF_DAYS,
  PERF_SERIES,
  SOURCE_STATUSES,
  SYNC_ACTIVITY,
  SYNC_HEALTH,
  SYNC_SOURCES,
  SYNC_STATS,
  SYNC_TYPES,
  TOTAL_SYNCS,
  TOTAL_SYNC_SOURCES,
} from "./data";
import "./DataIntegration.css";

export default function Synchronization() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return SYNC_SOURCES.filter((source) => {
      if (term && !`${source.name} ${source.code}`.toLowerCase().includes(term)) {
        return false;
      }
      if (status && source.status !== status) return false;
      if (type && source.type !== type) return false;
      return true;
    });
  }, [search, status, type]);

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Synchronization</h1>
          <p className="page__subtitle">
            Configure and monitor data synchronization between DGI Smart Tax
            Registry and external data sources.
          </p>
        </div>
        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <HistoryIcon size={17} />
            Synchronization History
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <PlusIcon size={17} />
            New Synchronization
          </button>
        </div>
      </div>

      <StatCards cards={SYNC_STATS} />

      <div className="di-main">
        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Synchronization Overview</h2>
          </div>

          <div className="di-filters">
            <div className="filters__search">
              <input
                type="search"
                placeholder="Search data sources..."
                aria-label="Search data sources"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
              <SearchIcon size={17} />
            </div>
            <span className="select select--block">
              <select
                value={status}
                aria-label="Filter by status"
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">All Statuses</option>
                {SOURCE_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>
            <span className="select select--block">
              <select
                value={type}
                aria-label="Filter by type"
                onChange={(event) => setType(event.target.value)}
              >
                <option value="">All Types</option>
                {SYNC_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>
            <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
              <FilterIcon size={16} />
              Filters
            </button>
          </div>

          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--sync">
              <thead>
                <tr>
                  <th>Data Source</th>
                  <th>Type</th>
                  <th>Frequency</th>
                  <th>Last Sync</th>
                  <th>Status</th>
                  <th>Records Synced</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((source) => {
                  const Icon = SOURCE_ICONS[source.icon];
                  return (
                    <tr key={source.code}>
                      <td>
                        <span className="src">
                          <span className={`src__icon kpi__icon--${source.tone}`}>
                            <Icon size={17} />
                          </span>
                          <span className="src__text">
                            <button type="button" className="link">
                              {source.name}
                            </button>
                            <span className="dgi-table__sub">{source.code}</span>
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className={chipClass(source.type)}>
                          {source.type}
                        </span>
                      </td>
                      <td>{source.frequency}</td>
                      <td>
                        {source.date}
                        <span className="dgi-table__sub">{source.time}</span>
                      </td>
                      <td>
                        <span
                          className={`chip chip--dot ${chipClass(source.status)}`}
                        >
                          {source.status}
                        </span>
                      </td>
                      <td>{source.records}</td>
                      <td>
                        <span className="row-actions">
                          <button
                            type="button"
                            className="row-actions__btn"
                            aria-label={`Run sync for ${source.name}`}
                          >
                            <PlayIcon size={15} />
                          </button>
                          <button
                            type="button"
                            className="dgi-card__more"
                            aria-label={`Actions for ${source.name}`}
                          >
                            <MoreIcon size={18} />
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="dgi-table__empty">
              No synchronizations match these filters.
            </p>
          )}

          <Pager
            page={page}
            setPage={setPage}
            perPage={perPage}
            setPerPage={setPerPage}
            total={TOTAL_SYNC_SOURCES}
            shown={rows.length}
            noun="data sources"
          />
        </section>

        <div className="stack">
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Synchronization Health</h2>
            </div>
            <DonutChart
              segments={SYNC_HEALTH}
              total={TOTAL_SYNCS}
              totalLabel="Total Syncs"
            />
            <button type="button" className="dgi-card__link">
              View synchronization history <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Sync Performance (Last 7 Days)</h2>
            </div>
            <MultiTrendChart
              labels={PERF_DAYS}
              series={PERF_SERIES}
              max={5}
              tickCount={5}
              formatTick={(value) => `${Math.round(value)}M`}
              formatValue={(value) => `${value.toFixed(2)}M records`}
            />
            <button type="button" className="dgi-card__link">
              View performance report <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>
      </div>

      <div className="di-main">
        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Recent Synchronization Activity</h2>
          </div>
          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--syncact">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Data Source</th>
                  <th>Status</th>
                  <th>Records</th>
                  <th>Duration</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {SYNC_ACTIVITY.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.time}</td>
                    <td>{entry.source}</td>
                    <td>
                      <span className={chipClass(entry.status)}>
                        {entry.status}
                      </span>
                    </td>
                    <td>{entry.records}</td>
                    <td>{entry.duration}</td>
                    <td>
                      <button
                        type="button"
                        className="row-actions__btn"
                        aria-label={`Details for ${entry.source}`}
                      >
                        <EyeIcon size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="dgi-card__link">
            View all synchronization history <ArrowRightIcon size={16} />
          </button>
        </section>

        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Next Scheduled Synchronizations</h2>
          </div>
          <ul className="schedule">
            {NEXT_SYNCS.map((item) => (
              <li className="schedule__row" key={item.key}>
                <span className="schedule__icon">
                  <ClockIcon size={17} />
                </span>
                <span className="schedule__label">{item.label}</span>
                <span className="schedule__when">{item.when}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="dgi-card__link">
            View full schedule <ArrowRightIcon size={16} />
          </button>
        </section>
      </div>
    </div>
  );
}
